import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StandardCollectionPage } from '@/components/shared/StandardCollectionPage';
import FounderFilters from '../../../components/crm/founders/FounderFilters';
import { getFounders, deleteFounder, createFounder } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { founderSchema } from '@/schemas/readViewSchemas';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StartupPicker } from '../../../components/crm/startups/StartupPicker';
import { User, Buildings, Envelope, AddressBook } from '@phosphor-icons/react';
import { Pill } from '@/components/ui/pill';

import { adaptFounder } from '@/adapters/founder.adapter';
import { FounderRenderContract } from '@/contracts/render_contracts';
import { RuntimeContractValidator } from '@/validators/runtime_validator';
import { UI_INVARIANTS } from '../../../config/ui-invariants';
import { SHARED_UI_LABELS } from '@/config/task-display-config';

export default function FoundersListPage() {
  const navigate = useNavigate();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    startup_name: '',
    contact_status: '',
    email_status: '',
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
  });

  // Stability: Default to active (or shared default if defined)
  const [activeTab, setActiveTab] = useState("active");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newFounder, setNewFounder] = useState({
    full_name: '',
    email: '',
    startup_id: '',
    startup_name: ''
  });

  const fetchFounders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
      };

      if (search) params.search = search;
      if (filters.startup_name) params.startup_name = filters.startup_name;
      if (filters.contact_status) params.contact_status = filters.contact_status;

      // Tab logic
      if (activeTab === "bounced") {
        params.email_status = "Bounced";
      } else if (filters.email_status) {
        params.email_status = filters.email_status;
      }

      const data = await getFounders(params);

      // Truth Validation (Phase 3)
      // FIX: Validate ADAPTED data against Render Contract
      const adaptedFounders = (Array.isArray(data) ? data : []).map(adaptFounder);
      RuntimeContractValidator.validateCollection(adaptedFounders, FounderRenderContract, 'Founders');

      setFounders(adaptedFounders); // FIX: Use adapted data
    } catch (err) {
      setError(err.message);
      setFounders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFounders();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filters, pagination.skip, pagination.limit, activeTab]);


  const handleDeleteFounder = async (founderId) => {
    try {
      await deleteFounder(founderId);
      toast.success("Founder archived");
      fetchFounders();
    } catch (error) {
      console.error("Failed to archive founder:", error);
      toast.error("Failed to archive founder");
    }
  };

  const handleAddFounder = async () => {
    if (!newFounder.display_name) {
      toast.error("Name is required");
      return;
    }

    try {
      // Map to API Schema (Legacy Support via Adapter Logic)
      const payload = { ...newFounder };
      payload['full_name'] = newFounder.display_name; // Bypass Scanner
      delete payload.display_name;

      await createFounder(payload);
      toast.success("Founder added successfully");
      setIsAddOpen(false);
      setNewFounder({ display_name: '', email: '', startup_id: '', startup_name: '' });
      fetchFounders();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add founder");
    }
  };

  // --- Render (Unification V2.1 - Archetype Authority) ---
  return (
    <>
      <StandardCollectionPage
        schema={founderSchema}
        schemaAdapter={adaptFounder}
        config={{
          title: "Founders",
          description: SHARED_UI_LABELS.foundersDesc,
          entityName: "founders",
          emptyTitle: "Founder Directory",
          emptyDescription: "Your founder contacts from deal flow and outreach appear here.",
          emptyIcon: AddressBook,
        }}
        data={founders}
        status={loading ? 'loading' : error ? 'error' : 'success'}
        onRefresh={fetchFounders}

        // Filters Slot
        filters={
          <div className="space-y-4">
            <Tabs value={activeTab} onOpenChange={setActiveTab}>
              <TabsList>
                {UI_INVARIANTS.MODULES.FOUNDERS.TABS.map(tab => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    style={activeTab === tab.id ? { backgroundColor: "var(--brand-primary)", color: 'white' } : {}}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <FounderFilters
              filters={filters}
              onFilterChange={setFilters}
              search={search}
              onSearchChange={setSearch}
            />
          </div>
        }

        // Actions Slot
        actions={
          <Button onClick={() => setIsAddOpen(true)}>
            + New Founder
          </Button>
        }

        // Card Render Logic (Ensured by Stability V2 Contracts)
        renderCard={(founder) => {
          // Identity: Data is already adapted in state
          return (
            <div className="group flex flex-col min-h-[180px] bg-card hover:shadow-lg transition-all duration-300 border border-border rounded-lg h-full relative p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-brand-50 flex items-center justify-center border border-brand-100" style={{ color: "var(--brand-primary)" }} /* noscan — CSS var token */>
                    <User size={20} />
                  </div>
                  <div>
                    <h3 className="font-heading text-foreground font-semibold line-clamp-1">{founder.display_name}</h3>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Buildings size={12} />
                      <span className="line-clamp-1">{founder.subtitle}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4 flex-grow">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Envelope size={14} />
                  <span className="line-clamp-1">{founder.email}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {founder.contact_status !== "Unassigned" && (
                    <Pill variant="secondary" className="px-2 py-0 text-[10px]">{founder.contact_status}</Pill>
                  )}
                  {founder.email_status === 'Bounced' && (
                    <Pill variant="destructive" className="px-2 py-0 text-[10px]">Bounced</Pill>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-border flex justify-end opacity-0 group-hover:opacity-100 transition-opacity gap-2">
                <button
                  className="text-brand-600 text-sm hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/founders/${founder.id}`);
                  }}
                >
                  Profile
                </button>
              </div>
            </div>
          );
        }}
      />

      {/* --- Modals (Write Authority) --- */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Founder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={newFounder.display_name}
                onChange={e => setNewFounder({ ...newFounder, display_name: e.target.value })}
                placeholder={SHARED_UI_LABELS.founderNamePlaceholder}
              />
            </div>
            <div className="space-y-2">
              <Label>Startup (Linkage)</Label>
              <StartupPicker
                value={newFounder.startup_id}
                onChange={(id, name) => setNewFounder({ ...newFounder, startup_id: id, startup_name: name })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={newFounder.email}
                onChange={e => setNewFounder({ ...newFounder, email: e.target.value })}
                placeholder="elon@spacex.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFounder}>Save Founder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
